import React from "react";
import { Link as RouterLink } from "react-router-dom";
import walletIcon from "../../images/wallet-4.svg";
import moneyHandIcon from "../../images/money-hand, coins.svg";
import lockIcon from "../../images/lock, private.svg";
import sparklesIcon from "../../images/sparkles-three, ai 3 stars, sparkles, ✨.svg";

const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };

const CARDS = [
	{
		icon: walletIcon,
		title: "Savings Account",
		subtext:
			"Build better money habits with secure savings, steady growth, and access when you need it.",
		to: "/saving-account",
	},
	{
		icon: moneyHandIcon,
		title: "Business Loans",
		subtext:
			"Get fast and flexible funding to restock inventory, expand operations, or keep your business moving forward with confidence.",
		to: "/loans",
	},
	{
		icon: lockIcon,
		title: "Fixed Deposits",
		subtext:
			"Lock your funds for a fixed period and enjoy higher returns than regular savings as your money grows securely.",
		to: "/fixed-deposit",
	},
	{
		icon: sparklesIcon,
		title: "Z-ForHer Credit Line",
		subtext:
			"Smart reusable credit designed for ambitious women growing their businesses.",
		to: "/z-for-her",
	},
];

const Card = ({ icon, title, subtext, to }) => (
	<div className="product-card" style={{ flex: "1 1 0", minWidth: 0 }}>
		{/* Icon box */}
		<div
			style={{
				width: "40px",
				height: "40px",
				backgroundColor: "#132A22",
				borderRadius: "8px",
				padding: "8px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexShrink: 0,
				boxSizing: "border-box",
			}}
		>
			<img
				src={icon}
				alt=""
				aria-hidden="true"
				style={{ width: "100%", height: "100%", objectFit: "contain" }}
			/>
		</div>

		{/* Text block */}
		<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
			<p className="card-title" style={{ fontFamily: "'Geist', sans-serif", fontWeight: 500, fontSize: "clamp(16px, 1.39vw, 20px)", lineHeight: "1.3", margin: 0 }}>
				{title}
			</p>
			<p className="card-subtext" style={{ fontFamily: "'Geist', sans-serif", fontWeight: 400, fontSize: "clamp(13px, 1.11vw, 16px)", lineHeight: "1.75", margin: 0 }}>
				{subtext}
			</p>
			<RouterLink
				to={to}
				className="card-link"
				style={{ fontFamily: "'Geist', sans-serif", fontWeight: 400, fontSize: "clamp(13px, 1.11vw, 16px)", lineHeight: "1", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "6px" }}
			>
				Learn more →
			</RouterLink>
		</div>
	</div>
);

const ProductOverview = () => {
	return (
		<>
			<style>{`
				.product-card {
					background-color: #F4F2EC;
					border-radius: 8px;
					padding: clamp(20px, 2.22vw, 32px) clamp(20px, 1.67vw, 28px);
					display: flex;
					flex-direction: column;
					justify-content: space-between;
					gap: clamp(32px, 3.33vw, 48px);
					box-sizing: border-box;
					flex-grow: 1;
					transition: flex-grow 0.3s ease, background-color 0.3s ease;
					cursor: pointer;
				}
				.product-card:hover {
					background-color: #132A22;
					flex-grow: 1.3;
				}
				.card-title { color: #132A22; transition: color 0.3s ease; }
				.product-card:hover .card-title { color: #FFFFFF; }
				.card-subtext { color: #3D4D3D; transition: color 0.3s ease; }
				.product-card:hover .card-subtext { color: #A3C4AD; }
				.card-link { color: #295B4A; transition: color 0.3s ease; }
				.product-card:hover .card-link { color: #DEDEDE; }
				@media (max-width: 767px) {
					.product-cards-row {
						flex-direction: column !important;
					}
					.product-card {
						flex: 1 1 auto !important;
						padding: 16px !important;
						gap: 20px !important;
					}
					.product-card:hover {
						flex-grow: 1 !important;
					}
					.card-title {
						font-size: 14px !important;
						line-height: 20px !important;
					}
					.card-subtext {
						font-size: 12px !important;
						line-height: 24px !important;
					}
					.card-link {
						font-size: 12px !important;
					}
				}
			`}</style>

			<section
				style={{
					width: "100%",
					backgroundColor: "#FFFFFF",
					padding: "clamp(48px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
					boxSizing: "border-box",
				}}
			>
				<div style={{ maxWidth: "1440px", margin: "0 auto" }}>
					{/* Section header */}
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "clamp(12px, 1.39vw, 20px)",
							marginBottom: "clamp(32px, 3.33vw, 48px)",
						}}
					>
						{/* Label */}
						<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
							<span
								style={{
									width: "8px",
									height: "8px",
									borderRadius: "50%",
									backgroundColor: "#295B4A",
									flexShrink: 0,
									display: "inline-block",
								}}
							/>
							<span
								style={{
									...geistMono,
									fontWeight: 500,
									fontSize: "14px",
									lineHeight: "32px",
									letterSpacing: "0.08em",
									color: "#295B4A",
									textTransform: "uppercase",
									whiteSpace: "nowrap",
								}}
							>
								Products
							</span>
						</div>

						{/* Heading */}
						<h2
							style={{
								...instrumentSerif,
								fontWeight: 400,
								fontSize: "clamp(32px, 4.17vw, 60px)",
								lineHeight: "1.1",
								letterSpacing: "-0.01em",
								color: "#132A22",
								margin: 0,
								maxWidth: "680px",
							}}
						>
							Banking that works for how you earn, save and grow.
						</h2>

						{/* Subtitle */}
						<p
							style={{
								fontFamily: "'Geist', sans-serif",
								fontWeight: 400,
								fontSize: "20px",
								lineHeight: "32px",
								letterSpacing: "0px",
								color: "#5E5E5E",
								margin: 0,
							}}
						>
							From personal savings to business funding, choose what fits your
							next move.
						</p>
					</div>

					{/* Cards */}
					<div
						style={{
							width: "100%",
							display: "flex",
							flexDirection: "column",
							gap: "clamp(10px, 1.11vw, 16px)",
						}}
					>
						<div
							className="product-cards-row"
							style={{ display: "flex", gap: "clamp(10px, 1.11vw, 16px)" }}
						>
							<Card {...CARDS[0]} />
							<Card {...CARDS[1]} />
						</div>
						<div
							className="product-cards-row"
							style={{ display: "flex", gap: "clamp(10px, 1.11vw, 16px)" }}
						>
							<Card {...CARDS[2]} />
							<Card {...CARDS[3]} />
						</div>
					</div>
				</div>
			</section>
		</>
	);
};

export default ProductOverview;
